import React, { useMemo } from "react";
import Select from "react-select";
import { FormFeedback, FormGroup } from "reactstrap";

import { tooltips } from "../../../../lib/utils/tooltips";
import { selectStyles } from "../../../../lib/utils/selectStyles";
import { Providers } from "../../../../lib/context/Calendar/CalendarContextProvider";
import { CalendarType } from "../../../../api/grpc/calendarprovider/calendarprovider";
import {
  LabelDirectionType,
  LabelWithInformationBox,
} from "../../../shared/LabelWithInformationBox/LabelWithInformationBox";

interface Props {
  value: string;
  error?: string;
  providers: Providers[];
  onRemove: () => void;
  onChange: (value: string, type: CalendarType) => void;
  direction?: LabelDirectionType;
  label?: string;
  disabled?: boolean;
}

export const CalendarProvider = (props: Props) => {
  const {
    value,
    error,
    providers,
    onChange,
    onRemove,
    direction,
    label,
    disabled,
  } = props;

  const sortedProviders = useMemo(() => {
    const collator = new Intl.Collator(undefined, {
      sensitivity: "base",
      numeric: true,
    });

    return [...providers].sort((a, b) => collator.compare(a.name, b.name));
  }, [providers]);

  const selectValue =
    value && sortedProviders
      ? sortedProviders
          .filter((item) => item.iD === value)
          .map((item) => ({ label: item.name, value: item.iD }))
      : [];

  const options =
    sortedProviders.map((item) => ({ label: item.name, value: item.iD })) ||
    [];

  return (
    <FormGroup>
      <LabelWithInformationBox
        title={label || "Calendar source"}
        for="calendarProviderId"
        message={tooltips.rooms.settings.calendarSource}
        direction={direction}
      />
      <Select
        isClearable
        isDisabled={disabled}
        name="calendarId"
        options={options}
        menuPlacement="top"
        value={selectValue}
        theme={selectStyles}
        onChange={(e) => {
          if (e === null) {
            onRemove();
            return;
          }

          const provider = sortedProviders.find((item) => item.iD === e.value);

          // Typescript doing its thing
          if (!provider) {
            return;
          }

          onChange(provider.iD, provider.calType);
        }}
      />
      {!!error && (
        <FormFeedback className="invalid-feedback">{error}</FormFeedback>
      )}
    </FormGroup>
  );
};
